import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Section } from '@factorialco/deprecated-design-system'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import useUpdateAttendanceSetting from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateAttendanceSetting'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import AttendanceSettingForm from 'modules/attendance/lib/forms/AttendanceSettingForm'
import { AmplitudeProps } from 'modules/attendance/lib/types'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import { ExtraHoursBaselineOptions } from 'modules/attendance/store/models/AttendanceSetting'

import HoursTypeCard from './HoursTypeCard'

export interface Props {
  attendanceSetting: AttendanceSetting
}

const ExtraAndComplementaryHoursCalculation = ({ attendanceSetting }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { CONTRACT_HOURS } = ExtraHoursBaselineOptions
  const { refetch } = useAttendanceSettingWorkCategories()

  const createExtraHoursForm = (setting: AttendanceSetting) =>
    new AttendanceSettingForm({
      extra_hours_baseline: setting.attributes.extra_hours_baseline,
    })

  const createComplementaryHoursForm = (setting: AttendanceSetting) =>
    new AttendanceSettingForm({
      complementary_hours_baseline:
        setting.attributes.complementary_hours_baseline ?? CONTRACT_HOURS,
      enable_complementary_hours: setting.enableComplementaryHours,
    })

  const [extraHoursForm, setExtraHoursForm] = React.useState(
    createExtraHoursForm(attendanceSetting)
  )
  const [complementaryHoursForm, setComplementaryHoursForm] = React.useState(
    createComplementaryHoursForm(attendanceSetting)
  )

  const extraHoursBaselineField = extraHoursForm.get('extra_hours_baseline')
  const complementaryHoursBaselineField = complementaryHoursForm.get('complementary_hours_baseline')
  const enableComplementaryHoursField = complementaryHoursForm.get('enable_complementary_hours')

  const { loading: loadingExtraHours, updateSetting: updateExtraHours } =
    useUpdateAttendanceSetting(extraHoursForm, attendanceSetting)
  const { loading: loadingComplementaryHours, updateSetting: updateComplementaryHours } =
    useUpdateAttendanceSetting(complementaryHoursForm, attendanceSetting, false, refetch)

  const [extraHoursBaseline, setExtraHoursBaseline] = React.useState(
    attendanceSetting.extraHoursBaseline
  )
  const [complementaryHoursBaseline, setComplementaryHoursBaseline] = React.useState(
    attendanceSetting.complementaryHoursBaseline ?? CONTRACT_HOURS
  )
  const [complementaryHoursEnabled, setComplementaryHoursEnabled] = React.useState(
    attendanceSetting.enableComplementaryHours
  )

  const extraHoursEnabled = true
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const [pendingUpdate, setPendingUpdate] = React.useState<'extra' | 'complementary' | null>(null)

  const settingId = attendanceSetting.attributes.id
  const initialExtraHoursBaseline = attendanceSetting.extraHoursBaseline
  const initialComplementaryHoursBaseline =
    attendanceSetting.complementaryHoursBaseline ?? CONTRACT_HOURS
  const initialComplementaryHoursEnabled = attendanceSetting.enableComplementaryHours

  useEffect(() => {
    setExtraHoursForm(createExtraHoursForm(attendanceSetting))
    setExtraHoursBaseline(initialExtraHoursBaseline)
  }, [settingId, initialExtraHoursBaseline, attendanceSetting])

  useEffect(() => {
    setComplementaryHoursForm(createComplementaryHoursForm(attendanceSetting))
    setComplementaryHoursBaseline(initialComplementaryHoursBaseline)
    setComplementaryHoursEnabled(initialComplementaryHoursEnabled)
    attendanceSetting.attributes.enable_complementary_hours = initialComplementaryHoursEnabled
  }, [
    settingId,
    initialComplementaryHoursBaseline,
    initialComplementaryHoursEnabled,
    attendanceSetting,
  ])

  const onSubmit = () => {
    if (pendingUpdate === 'extra') {
      updateExtraHours()
    } else if (pendingUpdate === 'complementary') {
      // When complementary hours is disabled, ensure baseline is null before saving
      if (!complementaryHoursEnabled) {
        complementaryHoursBaselineField.set(null)
      }
      updateComplementaryHours()
    }
    onClose()
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    onSubmit()

    const routeParams = {
      id: 'time_tracking',
      settingId: String(newSetting.id),
      tabId: 'work_time_categories',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onChangeExtraHoursBaseline = (option: string) => {
    if (Object.values(ExtraHoursBaselineOptions).includes(option)) {
      extraHoursBaselineField.set(option)
      setExtraHoursBaseline(option)
    }
  }

  const onChangeComplementaryHoursBaseline = (option: string) => {
    if (Object.values(ExtraHoursBaselineOptions).includes(option)) {
      complementaryHoursBaselineField.set(option)
      setComplementaryHoursBaseline(option)
      track(trackingEvents.timeTracking.selectReferencePointComplementaryHours, {
        option,
      })
    }
  }

  const onClose = () => {
    setOpenEffectiveAtFormModal(false)
    setPendingUpdate(null)
  }

  return (
    <Section
      illustration="performance"
      title={i18n.t(
        'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.section.title'
      )}
      description={i18n.t(
        'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.section.description'
      )}
    >
      <Box gap="s8">
        <Box gap="s16">
          <HoursTypeCard
            title={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.extra_hours.title'
            )}
            description={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.extra_hours.description'
            )}
            referencePoint={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.extra_hours.reference_point'
            )}
            referencePointDescription={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.extra_hours.reference_point_description'
            )}
            radioNamePrefix="time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.extra_hours.title"
            enabled={extraHoursEnabled}
            selectedBaseline={extraHoursBaseline}
            onBaselineChange={onChangeExtraHoursBaseline}
          />

          {extraHoursForm.isDirty && (
            <Box alignItems="flexEnd">
              <Button
                type="primary"
                onClick={() => {
                  setPendingUpdate('extra')
                  setOpenEffectiveAtFormModal(true)
                }}
                disabled={loadingExtraHours}
              >
                {i18n.t('common.save')}
              </Button>
            </Box>
          )}
        </Box>

        <Box gap="s16">
          <HoursTypeCard
            title={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.complementary_hours.title'
            )}
            description={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.complementary_hours.description'
            )}
            referencePoint={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.complementary_hours.reference_point'
            )}
            referencePointDescription={i18n.t(
              'time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.complementary_hours.reference_point_description'
            )}
            radioNamePrefix="time_tracking.settings.work_time_categories.extra_and_complementary_hours_calculation.complementary_hours.title"
            showToggle
            enabled={complementaryHoursEnabled}
            onToggle={(enabled) => {
              setComplementaryHoursEnabled(enabled)
              enableComplementaryHoursField.set(enabled)

              track(trackingEvents.timeTracking.toggleComplementaryHours, {
                enabled: enabled,
              })

              if (!enabled) {
                complementaryHoursBaselineField.set(null)
              }

              // If toggle is back to original state, reset form to clear dirty state
              if (enabled === initialComplementaryHoursEnabled && complementaryHoursForm.isDirty) {
                setComplementaryHoursForm(createComplementaryHoursForm(attendanceSetting))
                setComplementaryHoursBaseline(initialComplementaryHoursBaseline)
              }
            }}
            selectedBaseline={complementaryHoursBaseline}
            onBaselineChange={onChangeComplementaryHoursBaseline}
          />

          {complementaryHoursForm.isDirty && (
            <Box alignItems="flexEnd">
              <Button
                type="primary"
                onClick={() => {
                  setPendingUpdate('complementary')
                  setOpenEffectiveAtFormModal(true)
                }}
                disabled={loadingComplementaryHours}
              >
                {i18n.t('common.save')}
              </Button>
            </Box>
          )}
        </Box>

        {openEffectiveAtFormModal && (
          <ManageSettingVersionModal
            onCreate={onCreateSetting}
            onUpdate={onSubmit}
            onClose={onClose}
            running={pendingUpdate === 'extra' ? loadingExtraHours : loadingComplementaryHours}
            attendanceSettingId={attendanceSetting.attributes.id}
            attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
            origin={
              pendingUpdate === 'complementary'
                ? AmplitudeProps.Origin.ComplementaryHours
                : AmplitudeProps.Origin.ExtraHours
            }
          />
        )}
      </Box>
    </Section>
  )
}

export default ExtraAndComplementaryHoursCalculation
